import { Assignee } from '@/components/shared/api/endpoints/applicantAssignees';
import { OrpUserProfile } from '@/components/shared/api/endpoints/slack';

import { PointOfContact } from '@/components/pages/Applicant/Applicant.types';
import { Condition } from '@/components/pages/Applicant/components/ApplicantConditions/ApplicantConditions.types';

import { MentionSource, MentionSuggestion } from './MentionTextarea.types';

/** Only TFIN addresses are mentionable — ORIG-747 only notifies these. */
const TFIN_DOMAIN = '@tfin.com';
const TBCAP_DOMAIN = '@tbcap.com';
const TBKBANK_DOMAIN = '@tbkbank.com';

/** Cap on rendered suggestions, per the plan (prevents overwhelming the UI). */
export const MAX_SUGGESTIONS = 8;

/**
 * The result of inspecting the text around the caret for an active `@` trigger.
 * Pure and DOM-free so it can be unit-tested without a browser — the caret
 * geometry lives separately in {@link useMentionTrigger}.
 */
export interface TriggerDetection {
    isActive: boolean;
    query: string;
    triggerIndex: number;
}

const INACTIVE: TriggerDetection = { isActive: false, query: '', triggerIndex: -1 };

/**
 * Decide whether the caret sits inside an open `@` mention, and if so extract
 * the query typed after it.
 *
 * Rules (mirrors the keyboard spec in the plan):
 * - The `@` must be at a word boundary: start of text, or preceded by
 *   whitespace. This is what keeps `email@tfin.com` from triggering mid-word.
 * - The query is every character between the `@` and the caret. Whitespace in
 *   that span closes the trigger (the user has moved on).
 *
 * @param text  Full text value of the input.
 * @param caret Caret offset (number of characters before the cursor).
 */
export function detectTrigger(text: string, caret: number): TriggerDetection {
    // Walk backwards from the caret to the nearest `@`. Bail the moment we hit
    // whitespace — a space between here and any `@` means we're not in a mention.
    for (let i = caret - 1; i >= 0; i--) {
        const char = text[i];
        if (char === '@') {
            const charBefore = i > 0 ? text[i - 1] : '';
            const atWordBoundary = i === 0 || /\s/.test(charBefore);
            if (!atWordBoundary) return INACTIVE;
            return { isActive: true, query: text.slice(i + 1, caret), triggerIndex: i };
        }
        if (/\s/.test(char)) return INACTIVE;
    }
    return INACTIVE;
}

export interface MentionInsertion {
    value: string;
    caret: number;
}

/**
 * Replace the `@{query}` span with the chosen email. The `@` is consumed — the
 * inserted text is just the plain email (no markup), which is exactly what the
 * ORIG-747 regex matches. A trailing space is added unless the text already
 * continues with whitespace, so consecutive mentions read naturally.
 *
 * @param text         Full text value.
 * @param triggerIndex Index of the `@`.
 * @param caret        Current caret offset (end of the query span).
 * @param email        Email to insert in place of `@{query}`.
 */
export function insertMention(
    text: string,
    triggerIndex: number,
    caret: number,
    email: string,
): MentionInsertion {
    const before = text.slice(0, triggerIndex);
    const after = text.slice(caret);
    const needsSpace = after.length === 0 || !/^\s/.test(after);
    const inserted = needsSpace ? `${email} ` : email;
    return { value: before + inserted + after, caret: (before + inserted).length };
}

const isInternalEmail = (email: string): boolean =>
    email.endsWith(TFIN_DOMAIN) ||
    email.endsWith(TBCAP_DOMAIN) ||
    email.endsWith(TBKBANK_DOMAIN);

/** The portion of an email before the `@`, used as the display label. */
const localPart = (email: string): string => email.split('@')[0];

/** Badge priority for ordering; lower sorts first. */
const SOURCE_ORDER: Record<MentionSource, number> = {
    assignee: 0,
    poc: 1,
    condition: 2,
    orpUser: 3,
};

/** Full name from an ORP profile, or empty string when no name parts exist. */
const profileName = (profile: OrpUserProfile): string =>
    profile.displayName ??
    profile.name ??
    [profile.firstName, profile.lastName].filter(Boolean).join(' ');

/**
 * Build the suggestion list from active ORP user profiles. A person is
 * mentionable only if they have a profile; the assignees, points of contact,
 * and condition-participant emails are used solely to pick the source badge,
 * defaulting to `orpUser`. Non-internal emails are excluded, duplicate profile
 * emails keep the first occurrence, and rows are ordered by badge priority then
 * label.
 */
export function buildMentionSuggestions(
    assignees: Assignee[],
    orpUserProfiles: OrpUserProfile[],
    pointOfContacts: PointOfContact[],
    conditions: Condition[],
): MentionSuggestion[] {
    // Key badge lookups by local part so a profile matches its list entry even
    // when the domains differ (e.g. jane@tfin.com vs jane@tbcap.com).
    const localPartSet = (emails: (string | null | undefined)[]): Set<string> =>
        new Set(
            emails
                .filter((e): e is string => Boolean(e))
                .map((e) => e.toLowerCase())
                .filter(isInternalEmail)
                .map(localPart),
        );

    const assigneeLocalParts = localPartSet(assignees.map((a) => a.email));
    const pocLocalParts = localPartSet(pointOfContacts.map((p) => p.email));
    const conditionLocalParts = localPartSet(
        conditions.flatMap((c) => [c.createdBy, ...(c.notes ?? []).map((n) => n.createdBy)]),
    );

    const sourceFor = (email: string): MentionSource => {
        const local = localPart(email);
        if (assigneeLocalParts.has(local)) return 'assignee';
        if (pocLocalParts.has(local)) return 'poc';
        if (conditionLocalParts.has(local)) return 'condition';
        return 'orpUser';
    };

    const suggestions: MentionSuggestion[] = [];
    const seen = new Set<string>();

    for (const profile of orpUserProfiles) {
        const email = profile.email.toLowerCase();
        if (!isInternalEmail(email) || seen.has(email)) continue;
        seen.add(email);
        suggestions.push({
            email,
            label: profileName(profile) || localPart(email),
            source: sourceFor(email),
            avatarUrl: profile.avatarUrl ?? undefined,
            title: profile.title ?? undefined,
        });
    }

    return suggestions.sort(
        (a, b) =>
            SOURCE_ORDER[a.source] - SOURCE_ORDER[b.source] || a.label.localeCompare(b.label),
    );
}

/**
 * Filter suggestions by the active query. Matches the query as a prefix of the
 * email and as a substring of the label, case-insensitively. An empty query
 * returns the full (capped) list. Capped at {@link MAX_SUGGESTIONS}.
 */
export function filterSuggestions(
    suggestions: MentionSuggestion[],
    query: string,
): MentionSuggestion[] {
    const normalized = query.toLowerCase().trim();
    const matches = normalized
        ? suggestions.filter(
              (s) =>
                  s.email.toLowerCase().startsWith(normalized) ||
                  s.label.toLowerCase().includes(normalized),
          )
        : suggestions;
    return matches.slice(0, MAX_SUGGESTIONS);
}
